import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  CHAT_ACCENT_IDS,
  persistChatAccent,
  readChatAccent,
  type ChatAccentId,
} from "@/lib/theme/chatAccent";
import {
  applyChatTheme,
  persistChatTheme,
  readChatTheme,
  toggleChatTheme,
  type ChatTheme,
} from "@/lib/theme/chatTheme";

type ChatThemeContextValue = {
  theme: ChatTheme;
  accent: ChatAccentId;
  setTheme: (theme: ChatTheme) => void;
  setAccent: (accent: ChatAccentId) => void;
  toggleTheme: () => void;
};

const ChatThemeContext = createContext<ChatThemeContextValue | null>(null);

export function ChatThemeProvider({
  children,
  forceTheme,
}: {
  children: ReactNode;
  forceTheme?: ChatTheme;
}) {
  const [theme, setThemeState] = useState<ChatTheme>(() => forceTheme ?? readChatTheme());
  const [accent, setAccentState] = useState<ChatAccentId>(() => readChatAccent());

  useEffect(() => {
    applyChatTheme(forceTheme ?? theme, accent);
  }, [forceTheme, theme, accent]);

  const setTheme = useCallback((next: ChatTheme) => {
    if (forceTheme) return;
    persistChatTheme(next);
    setThemeState(next);
  }, [forceTheme]);

  const setAccent = useCallback((next: ChatAccentId) => {
    if (!CHAT_ACCENT_IDS.includes(next)) return;
    persistChatAccent(next);
    setAccentState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    if (forceTheme) return;
    setThemeState((prev) => {
      const next = toggleChatTheme(prev);
      persistChatTheme(next);
      return next;
    });
  }, [forceTheme]);

  const value = useMemo(
    () => ({ theme: forceTheme ?? theme, accent, setTheme, setAccent, toggleTheme }),
    [forceTheme, theme, accent, setTheme, setAccent, toggleTheme]
  );

  return <ChatThemeContext.Provider value={value}>{children}</ChatThemeContext.Provider>;
}

export function useChatTheme(): ChatThemeContextValue {
  const ctx = useContext(ChatThemeContext);
  if (!ctx) {
    throw new Error("useChatTheme doit être utilisé dans ChatThemeProvider");
  }
  return ctx;
}
